import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional label for tracking which boundary caught the error */
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`ErrorBoundary${this.props.name ? ` [${this.props.name}]` : ''} caught:`, error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isDev = import.meta.env.DEV;

      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-8" role="alert">
          <div className="w-16 h-16 rounded-full bg-danger/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-danger" />
          </div>
          <h2 className="text-xl font-bold text-text mb-2">Something went wrong</h2>
          <p className="text-muted text-sm mb-1">
            An unexpected error occurred. Please try again.
          </p>
          {isDev && this.state.error?.message && (
            <pre className="text-danger/80 text-xs mt-2 mb-4 max-w-lg overflow-auto bg-white/5 rounded-lg p-3 text-left">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white rounded-full px-6 py-2 text-sm font-semibold transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 bg-transparent border border-white/20 hover:bg-white/5 text-text rounded-full px-6 py-2 text-sm font-semibold transition-colors"
            >
              Reload Page
            </button>
            <button
              onClick={this.handleGoHome}
              className="inline-flex items-center gap-2 bg-transparent hover:bg-white/5 text-muted hover:text-text rounded-full px-4 py-2 text-sm transition-colors"
              aria-label="Go to dashboard"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>
          <p className="text-muted text-xs mt-6">
            If the problem persists, contact{' '}
            <a href="mailto:support@enerlyze.co.za" className="text-primary hover:underline">
              support@enerlyze.co.za
            </a>
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
